/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { createStyles, ListItem, makeStyles, Theme, Typography, ListItemText } from "@material-ui/core";
import moment from "moment";
import React, { FC, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { createSelector } from "reselect";
import { Facility } from '../../db/stores/facilities';
import { AppState, FacilityMap, FollowUp, FollowUpsMap, ImmediateFollowUpData, nearestDeadline, redirect, StandardFollowUp } from "../../redux";
import { DATE_FORMAT_CONCISE, momentShortFormat } from "../../utils/time";

moment.updateLocale("en", momentShortFormat);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    listing: {
      backgroundColor: "#fff",
      display: "flex"
    },
    facilityIdentifier: {
      flex: 1,
    },
    facilityName: {},
    farmName: {
      color: theme.palette.text.secondary
    },
    deadlines: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
    },

    planText: {
      textIndent: "-1.5em",
      paddingLeft: "1.5em",
    },

    overdue: {
      color: theme.palette.error.main,
    },
    comingDue: {
      color: theme.palette.warning.main,
    },
    notComingDue: {
      color: theme.palette.text.disabled,
    },
    noDeadlines: {
      color: theme.palette.success.main
    }
  })
);

const selectFollowUps = (facilityId: string) =>
  createSelector<
    AppState,
    FollowUpsMap,
    FollowUp[]
  >(
    state => state.followUps.items,
    items => items.get(facilityId) ?? []
  );
const selectFacility = (facilityId: string) =>
  createSelector<
    AppState,
    FacilityMap,
    Facility
  >(
    state => state.facilities.items,
    items => items[facilityId]
  );

interface FollowUpFacilityListItemProps {
  facilityId: string;
}

export const FollowUpFacilityListItem: FC<FollowUpFacilityListItemProps> = (props) => {
  const { facilityId } = props;
  const facility = useSelector(selectFacility(facilityId))
  const facilityPlans = useSelector(selectFollowUps(facilityId));

  const classes = useStyles(props);

  const textStyler = useMemo(
    () => (deadline: string) => {

      if (!deadline) {
        return [classes.notComingDue, classes.planText].join(" ");
      }

      const overDue = moment(deadline).startOf('day').isBefore(now.startOf('day'));
      const daysUntilDue = now.diff(deadline, 'days');

      if (overDue) {
        return [classes.overdue, classes.planText].join(" ");
      } else if (daysUntilDue > -90) {
        return [classes.comingDue, classes.planText].join(" ");
      }

      return [classes.notComingDue, classes.planText].join(" ");
    },
    [classes]
  );

  // Banking on follow-ups already being ordered chronologically by deadline
  const nextIapDeadline = useMemo(() => {
    if (!facilityPlans || !facilityPlans.length) {
      return null;
    }
    const iNextIap = facilityPlans.findIndex(plan => plan.type === "iap");
    return iNextIap < 0 ? null : nearestDeadline(facilityPlans[iNextIap].data as ImmediateFollowUpData)
  },
    [facilityPlans]
  );
  const nextMcapDeadline = useMemo(
    () => {
      if (!facilityPlans || !facilityPlans.length) {
        return null;
      }
      const iNextMcap = facilityPlans.findIndex(plan => plan.type === "mcap");
      return iNextMcap < 0 ? null : (facilityPlans[iNextMcap] as StandardFollowUp).data.due
    },
    [facilityPlans]
  );
  const nextCipDeadline = useMemo(
    () => {
      if (!facilityPlans || !facilityPlans.length) {
        return null;
      }
      const iNextMcap = facilityPlans.findIndex(plan => plan.type === "cip");
      return iNextMcap < 0 ? null : (facilityPlans[iNextMcap] as StandardFollowUp).data.due
    },
    [facilityPlans]
  );

  const now = moment();
  const dispatch = useDispatch();
  const history = useHistory();

  return (
    <>
      <ListItem
        className={classes.listing}
        button
        onClick={() => dispatch(redirect(`/follow-ups/${facilityId}`, history))}
      >
        <ListItemText primary={facility?.name ?? '<Name missing>'} secondary={facility?.farm?.name ?? '<Name missing>'} className={classes.facilityIdentifier}></ListItemText>
        <div className={classes.deadlines}>
          {nextIapDeadline && (
            <Typography variant="caption" className={textStyler(nextIapDeadline)}>
              Next IAP due {moment(nextIapDeadline).format(DATE_FORMAT_CONCISE)}
            </Typography>
          )}
          {nextMcapDeadline && (
            <Typography variant="caption" className={textStyler(nextMcapDeadline)}>
              Next MCAP due {moment(nextMcapDeadline).format(DATE_FORMAT_CONCISE)}
            </Typography>
          )}
          {nextCipDeadline && (
            <Typography variant="caption" className={textStyler(nextCipDeadline)}>
              Next CIP due {moment(nextCipDeadline).format(DATE_FORMAT_CONCISE)}
            </Typography>
          )}
          {!nextIapDeadline && !nextMcapDeadline && !nextCipDeadline && (
            <Typography variant="caption" className={classes.noDeadlines}>
              There are no remaining outstanding action plans.
            </Typography>
          )}
        </div>
      </ListItem>
    </>
  );
};

export default FollowUpFacilityListItem;
